import React from "react";
import { Link } from "react-router-dom";

/**
 * Contains a render crash to the subtree that caused it.
 *
 * Without a boundary, one bad value unmounts the whole tree: React tears down
 * `<AuthProvider>` too, the session in memory is lost, and the user is bounced
 * back to the login screen. That is what makes a rendering bug look like
 * "the app logged me out", and it also destroys the stack trace needed to find
 * it. Wrapping the shell keeps the session intact and shows where it broke.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    if (process.env.NODE_ENV === "production") {
      // eslint-disable-next-line no-console
      console.error("Unhandled render error:", error, info?.componentStack);
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="max-w-3xl mx-auto p-8" data-testid="error-boundary">
        <div className="card-flat p-6 border-red-200 bg-red-50/40">
          <h1 className="text-lg font-medium text-red-800">
            This page could not be displayed
          </h1>
          <p className="text-sm text-gray-600 mt-1.5">
            The rest of the app is still signed in — only this view failed.
          </p>

          <pre
            className="mt-4 p-3 bg-white border border-[var(--border)] rounded text-[11px] overflow-auto max-h-48 whitespace-pre-wrap"
            data-testid="error-boundary-message"
          >
            {error?.message || String(error)}
          </pre>

          {info?.componentStack ? (
            <details className="mt-3">
              <summary className="text-xs text-gray-600 cursor-pointer">
                Component stack
              </summary>
              <pre className="mt-2 p-3 bg-white border border-[var(--border)] rounded text-[11px] overflow-auto max-h-64 whitespace-pre-wrap">
                {info.componentStack}
              </pre>
            </details>
          ) : null}

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null, info: null })}
              className="btn-primary text-sm h-9 px-4"
            >
              Try again
            </button>
            {/* Client-side navigation, not a full document load: reloading
                drops the in-memory access token and sends the user back to
                /login, which is indistinguishable from the bug we are
                recovering from.

                onClick is required even though navigation re-renders: this
                boundary wraps <AppLayout />, which stays mounted across
                /app/* routes, so changing the URL alone does not clear
                `error` and the fallback would keep rendering. It matters
                most when the crash happened on /app itself, where the target
                path is unchanged. Navigations that *do* change the path are
                handled by remounting the boundary on pathname (see App.js). */}
            <Link
              to="/app"
              onClick={() => this.setState({ error: null, info: null })}
              className="text-sm h-9 px-4 leading-9 border border-[var(--border)] rounded-md hover:bg-gray-50 inline-flex items-center"
              data-testid="error-boundary-back"
            >
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}